import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '../queryClient';
import { supabase } from '../services/supabase';

export type SocialPlatform = 'facebook' | 'instagram' | 'youtube';
export type SocialAccountStatus = 'connected' | 'expired' | 'revoked' | 'error';
export type SocialWorkflowStatus =
  | 'draft'
  | 'pending_approval'
  | 'approved'
  | 'rejected'
  | 'scheduled'
  | 'publishing'
  | 'published'
  | 'failed'
  | 'cancelled';

export interface SocialAccount {
  id: string;
  platform: SocialPlatform;
  account_external_id: string;
  display_name: string;
  username: string | null;
  avatar_url: string | null;
  page_id: string | null;
  metadata: Record<string, unknown>;
  status: SocialAccountStatus;
  created_at: string;
  updated_at: string;
}

export interface SocialMediaAsset {
  id: string;
  owner_user_id: string | null;
  file_name: string;
  mime_type: string;
  file_size_bytes: number | null;
  storage_bucket: string;
  storage_path: string;
  width: number | null;
  height: number | null;
  duration_seconds: number | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface SocialPost {
  id: string;
  title: string | null;
  body: string | null;
  youtube_video_title: string | null;
  youtube_video_description: string | null;
  primary_asset_id: string | null;
  metadata: Record<string, unknown>;
  workflow_status: SocialWorkflowStatus;
  scheduled_for: string | null;
  approved_at: string | null;
  approved_by: string | null;
  rejected_at: string | null;
  rejected_by: string | null;
  rejection_reason: string | null;
  published_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface SocialPostTarget {
  id: string;
  post_id: string;
  account_id: string;
  platform: SocialPlatform;
  status: 'pending' | 'queued' | 'publishing' | 'published' | 'failed' | 'skipped' | 'cancelled';
  platform_post_id: string | null;
  platform_permalink: string | null;
  published_at: string | null;
  last_error: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface SocialPostApproval {
  id: string;
  post_id: string;
  decision: 'pending' | 'approved' | 'rejected';
  comment: string | null;
  decided_by: string | null;
  decided_at: string | null;
  created_at: string;
}

export interface SocialAnalyticsSnapshot {
  id: string;
  post_id: string;
  target_id: string | null;
  platform: SocialPlatform;
  platform_post_id: string | null;
  snapshot_at: string;
  impressions: number;
  reach: number;
  likes: number;
  comments: number;
  shares: number;
  clicks: number;
  watch_time_seconds: number;
  metadata: Record<string, unknown>;
}

const TABLES = {
  accounts: 'social_accounts',
  posts: 'social_posts',
  targets: 'social_post_targets',
  approvals: 'social_post_approvals',
  jobs: 'social_publish_jobs',
  attempts: 'social_publish_attempts',
  assets: 'social_media_assets',
  analytics: 'social_post_analytics',
} as const;

const KEY = {
  accounts: ['social', 'accounts'] as const,
  posts: ['social', 'posts'] as const,
  post: (postId: string) => ['social', 'post', postId] as const,
  targets: (postId: string) => ['social', 'targets', postId] as const,
  approvals: (postId: string) => ['social', 'approvals', postId] as const,
  calendar: ['social', 'calendar'] as const,
  assets: ['social', 'assets'] as const,
  analytics: ['social', 'analytics'] as const,
  attempts: (postId: string) => ['social', 'attempts', postId] as const,
} as const;

const invalidateSocial = async () => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: KEY.posts }),
    queryClient.invalidateQueries({ queryKey: KEY.calendar }),
    queryClient.invalidateQueries({ queryKey: KEY.analytics }),
  ]);
};

export const useSocialAccounts = () =>
  useQuery<SocialAccount[]>({
    queryKey: KEY.accounts,
    queryFn: async () => {
      const { data, error } = await supabase
        .from(TABLES.accounts)
        .select('*')
        .order('platform', { ascending: true })
        .order('display_name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as SocialAccount[];
    },
    staleTime: 30_000,
  });

export const useInitSocialOAuth = () =>
  useMutation({
    mutationFn: async (input: { platform: SocialPlatform; redirectUri: string }) => {
      const { data, error } = await supabase.functions.invoke('social-oauth-init', {
        body: input,
      });
      if (error) throw error;
      return data as { authUrl: string; state: string };
    },
  });

export const useCompleteSocialOAuth = () =>
  useMutation({
    mutationFn: async (input: { code: string; state: string; platform?: SocialPlatform; redirectUri?: string }) => {
      const { data, error } = await supabase.functions.invoke('social-oauth-callback', {
        body: input,
      });
      if (error) throw error;
      return data as { success: boolean; linkedAccounts: Array<{ id: string; platform: SocialPlatform; displayName: string }> };
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY.accounts }),
  });

export const useDeleteSocialAccount = () =>
  useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from(TABLES.accounts).delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY.accounts }),
  });

export const isMobileFeedImportEnabled = (metadata: Record<string, unknown> | null | undefined): boolean => {
  const rawValue = metadata?.mobile_feed_import_enabled;
  if (rawValue === true) return true;
  if (typeof rawValue === 'string') return rawValue.trim().toLowerCase() === 'true';
  return false;
};

export const useSetMobileFeedImportEnabled = () =>
  useMutation({
    mutationFn: async (input: {
      id: string;
      metadata: Record<string, unknown> | null | undefined;
      enabled: boolean;
    }) => {
      const nextMetadata = {
        ...(input.metadata ?? {}),
        mobile_feed_import_enabled: input.enabled,
      };
      const { error } = await supabase.from(TABLES.accounts).update({ metadata: nextMetadata }).eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY.accounts }),
  });

export const useSocialPosts = () =>
  useQuery<SocialPost[]>({
    queryKey: KEY.posts,
    queryFn: async () => {
      const { data, error } = await supabase
        .from(TABLES.posts)
        .select('*')
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as SocialPost[];
    },
    staleTime: 15_000,
  });

export const useSocialPost = (postId?: string) =>
  useQuery<SocialPost | null>({
    queryKey: KEY.post(postId ?? 'none'),
    queryFn: async () => {
      if (!postId) return null;
      const { data, error } = await supabase.from(TABLES.posts).select('*').eq('id', postId).maybeSingle();
      if (error) throw error;
      return (data ?? null) as SocialPost | null;
    },
    staleTime: 10_000,
  });

export const useCreateSocialPost = () =>
  useMutation({
    mutationFn: async (input: {
      title?: string | null;
      body?: string | null;
      youtubeVideoTitle?: string | null;
      youtubeVideoDescription?: string | null;
      primaryAssetId?: string | null;
      metadata?: Record<string, unknown>;
      scheduledFor?: string | null;
      workflowStatus?: SocialWorkflowStatus;
      targetAccountIds?: string[];
    }) => {
      const { data, error } = await supabase
        .from(TABLES.posts)
        .insert({
          title: input.title ?? null,
          body: input.body ?? null,
          youtube_video_title: input.youtubeVideoTitle ?? null,
          youtube_video_description: input.youtubeVideoDescription ?? null,
          primary_asset_id: input.primaryAssetId ?? null,
          metadata: input.metadata ?? {},
          scheduled_for: input.scheduledFor ?? null,
          workflow_status: input.workflowStatus ?? 'draft',
        })
        .select('*')
        .single();
      if (error) throw error;

      if ((input.targetAccountIds ?? []).length > 0) {
        const { data: accountRows, error: accountErr } = await supabase
          .from(TABLES.accounts)
          .select('id, platform')
          .in('id', input.targetAccountIds ?? []);
        if (accountErr) throw accountErr;
        const rows = (accountRows ?? []).map((account) => ({
          post_id: data.id,
          account_id: account.id,
          platform: account.platform,
        }));
        if (rows.length > 0) {
          const { error: targetErr } = await supabase.from(TABLES.targets).insert(rows);
          if (targetErr) throw targetErr;
        }
      }

      return data as SocialPost;
    },
    onSuccess: async () => {
      await invalidateSocial();
      await queryClient.invalidateQueries({ queryKey: KEY.targets('') });
    },
  });

export const useUpdateSocialPost = () =>
  useMutation({
    mutationFn: async (input: {
      id: string;
      changes: Partial<
        Pick<
          SocialPost,
          | 'title'
          | 'body'
          | 'youtube_video_title'
          | 'youtube_video_description'
          | 'primary_asset_id'
          | 'metadata'
          | 'scheduled_for'
          | 'workflow_status'
          | 'rejection_reason'
        >
      >;
    }) => {
      const { data, error } = await supabase
        .from(TABLES.posts)
        .update(input.changes)
        .eq('id', input.id)
        .select('*')
        .single();
      if (error) throw error;
      return data as SocialPost;
    },
    onSuccess: async (_data, input) => {
      await invalidateSocial();
      await queryClient.invalidateQueries({ queryKey: KEY.post(input.id) });
    },
  });

export const useDeleteSocialPost = () =>
  useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from(TABLES.posts).delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: invalidateSocial,
  });

export const useSocialPostTargets = (postId?: string) =>
  useQuery<SocialPostTarget[]>({
    queryKey: KEY.targets(postId ?? 'none'),
    queryFn: async () => {
      if (!postId) return [];
      const { data, error } = await supabase
        .from(TABLES.targets)
        .select('*')
        .eq('post_id', postId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as SocialPostTarget[];
    },
    staleTime: 10_000,
  });

export const useReplaceSocialPostTargets = () =>
  useMutation({
    mutationFn: async (input: { postId: string; accountIds: string[] }) => {
      const { error: delError } = await supabase.from(TABLES.targets).delete().eq('post_id', input.postId);
      if (delError) throw delError;
      if (input.accountIds.length === 0) return;

      const { data: accounts, error: accountErr } = await supabase
        .from(TABLES.accounts)
        .select('id, platform')
        .in('id', input.accountIds);
      if (accountErr) throw accountErr;
      const rows = (accounts ?? []).map((account) => ({
        post_id: input.postId,
        account_id: account.id,
        platform: account.platform,
      }));
      if (rows.length > 0) {
        const { error: insertError } = await supabase.from(TABLES.targets).insert(rows);
        if (insertError) throw insertError;
      }
    },
    onSuccess: async (_data, input) => {
      await queryClient.invalidateQueries({ queryKey: KEY.targets(input.postId) });
      await invalidateSocial();
    },
  });

export const useSocialPostApprovals = (postId?: string) =>
  useQuery<SocialPostApproval[]>({
    queryKey: KEY.approvals(postId ?? 'none'),
    queryFn: async () => {
      if (!postId) return [];
      const { data, error } = await supabase
        .from(TABLES.approvals)
        .select('*')
        .eq('post_id', postId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as SocialPostApproval[];
    },
    staleTime: 10_000,
  });

export const useSubmitSocialPostForApproval = () =>
  useMutation({
    mutationFn: async (postId: string) => {
      const { error: postErr } = await supabase
        .from(TABLES.posts)
        .update({ workflow_status: 'pending_approval', rejection_reason: null })
        .eq('id', postId);
      if (postErr) throw postErr;
      const { error: approvalErr } = await supabase.from(TABLES.approvals).insert({
        post_id: postId,
        decision: 'pending',
      });
      if (approvalErr) throw approvalErr;
    },
    onSuccess: invalidateSocial,
  });

export const useDecideSocialPostApproval = () =>
  useMutation({
    mutationFn: async (input: { postId: string; decision: 'approved' | 'rejected'; comment?: string | null }) => {
      const now = new Date().toISOString();
      const { data: authData } = await supabase.auth.getUser();
      const userId = authData.user?.id ?? null;
      const { error: approvalError } = await supabase.from(TABLES.approvals).insert({
        post_id: input.postId,
        decision: input.decision,
        comment: input.comment ?? null,
        decided_at: now,
        decided_by: userId,
      });
      if (approvalError) throw approvalError;

      if (input.decision === 'approved') {
        const { data: post, error: postErr } = await supabase
          .from(TABLES.posts)
          .select('scheduled_for')
          .eq('id', input.postId)
          .single();
        if (postErr) throw postErr;
        const nextStatus: SocialWorkflowStatus = post.scheduled_for ? 'scheduled' : 'approved';
        const { error: upErr } = await supabase
          .from(TABLES.posts)
          .update({
            workflow_status: nextStatus,
            approved_at: now,
            approved_by: userId,
            rejected_at: null,
            rejected_by: null,
            rejection_reason: null,
          })
          .eq('id', input.postId);
        if (upErr) throw upErr;
      } else {
        const { error: rejectErr } = await supabase
          .from(TABLES.posts)
          .update({
            workflow_status: 'rejected',
            rejected_at: now,
            rejected_by: userId,
            rejection_reason: input.comment ?? 'Rejected in approval workflow.',
          })
          .eq('id', input.postId);
        if (rejectErr) throw rejectErr;
      }
    },
    onSuccess: invalidateSocial,
  });

export const useScheduleSocialPost = () =>
  useMutation({
    mutationFn: async (input: { postId: string; scheduledFor: string }) => {
      const { error } = await supabase
        .from(TABLES.posts)
        .update({ scheduled_for: input.scheduledFor, workflow_status: 'scheduled' })
        .eq('id', input.postId);
      if (error) throw error;
    },
    onSuccess: invalidateSocial,
  });

export const useRunSocialDispatchNow = () =>
  useMutation({
    mutationFn: async (input?: { limit?: number }) => {
      const { data, error } = await supabase.functions.invoke('social-schedule-dispatch', {
        body: { limit: input?.limit ?? 20, runWorkerNow: true },
      });
      if (error) throw error;
      return data as { success: boolean; duePosts: number; enqueued: number; workerTriggered: boolean };
    },
    onSuccess: invalidateSocial,
  });

export const useSyncSocialAnalyticsNow = () =>
  useMutation({
    mutationFn: async (input?: { limit?: number }) => {
      const { data, error } = await supabase.functions.invoke('social-sync-analytics', {
        body: { limit: input?.limit ?? 100 },
      });
      if (error) throw error;
      return data as { success: boolean; scanned: number; synced: number };
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY.analytics }),
  });

export const useSocialCalendarPosts = () =>
  useQuery<SocialPost[]>({
    queryKey: KEY.calendar,
    queryFn: async () => {
      const { data, error } = await supabase
        .from(TABLES.posts)
        .select('*')
        .in('workflow_status', ['scheduled', 'publishing', 'published', 'failed'])
        .order('scheduled_for', { ascending: true, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as SocialPost[];
    },
    staleTime: 10_000,
  });

export const useSocialMediaAssets = () =>
  useQuery<SocialMediaAsset[]>({
    queryKey: KEY.assets,
    queryFn: async () => {
      const { data, error } = await supabase
        .from(TABLES.assets)
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as SocialMediaAsset[];
    },
    staleTime: 20_000,
  });

export const useCreateSocialMediaAsset = () =>
  useMutation({
    mutationFn: async (input: {
      fileName: string;
      mimeType: string;
      fileSizeBytes?: number | null;
      storagePath: string;
      width?: number | null;
      height?: number | null;
      durationSeconds?: number | null;
      metadata?: Record<string, unknown>;
    }) => {
      const { data, error } = await supabase
        .from(TABLES.assets)
        .insert({
          file_name: input.fileName,
          mime_type: input.mimeType,
          file_size_bytes: input.fileSizeBytes ?? null,
          storage_path: input.storagePath,
          width: input.width ?? null,
          height: input.height ?? null,
          duration_seconds: input.durationSeconds ?? null,
          metadata: input.metadata ?? {},
        })
        .select('*')
        .single();
      if (error) throw error;
      return data as SocialMediaAsset;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY.assets }),
  });

export const useDeleteSocialMediaAsset = () =>
  useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from(TABLES.assets).delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY.assets }),
  });

export const useSocialAnalytics = () =>
  useQuery<{
    snapshots: SocialAnalyticsSnapshot[];
    totals: {
      impressions: number;
      reach: number;
      likes: number;
      comments: number;
      shares: number;
      clicks: number;
    };
  }>({
    queryKey: KEY.analytics,
    queryFn: async () => {
      const { data, error } = await supabase
        .from(TABLES.analytics)
        .select('*')
        .order('snapshot_at', { ascending: false })
        .limit(500);
      if (error) throw error;
      const rows = (data ?? []) as SocialAnalyticsSnapshot[];
      const totals = rows.reduce(
        (acc, row) => ({
          impressions: acc.impressions + Number(row.impressions ?? 0),
          reach: acc.reach + Number(row.reach ?? 0),
          likes: acc.likes + Number(row.likes ?? 0),
          comments: acc.comments + Number(row.comments ?? 0),
          shares: acc.shares + Number(row.shares ?? 0),
          clicks: acc.clicks + Number(row.clicks ?? 0),
        }),
        { impressions: 0, reach: 0, likes: 0, comments: 0, shares: 0, clicks: 0 }
      );
      return { snapshots: rows, totals };
    },
    staleTime: 20_000,
  });

export const useSocialPublishAttempts = (postId?: string) =>
  useQuery({
    queryKey: KEY.attempts(postId ?? 'none'),
    queryFn: async () => {
      if (!postId) return [];
      const { data, error } = await supabase
        .from(TABLES.attempts)
        .select('*')
        .eq('post_id', postId)
        .order('attempted_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 10_000,
  });
